import { UserType } from "./types/userType";
import { generateAuthorizer, enumifyList } from "@coherehealth/common";
import useUserRoles from "./hooks/useUserRoles";

/**
 * Roles granted access to each feature type
 */
export const Permissions = enumifyList([
  "QM_PILOT",
  "QM_USER",
  "SUPERVISOR",
  "QM_MANAGER",
  "PRODUCTIVITY_REPORTS",
  "AUDIT_REVIEW",
  "FIND_A_CASE",
] as const);

type Feature = keyof typeof Permissions;

export function useAuthorized(feature: Feature): boolean {
  const userRoles = useUserRoles();

  if (userRoles === undefined || userRoles.length === 0) {
    return false;
  }

  return isAuthorized(feature, userRoles);
}

// FIND_A_CASE is a broad compile-time ceiling: granted to ops, supervisors, managers and all
// reviewers so that broadening the audience later is a LaunchDarkly targeting change (qmFindACase)
// with zero code. It is added alongside QM_USER on every leaf that grants it, on the direct-perm
// entries (CohereEngOnCall/CoherePm), and — because AuditReviewer grants only AUDIT_REVIEW and does
// NOT inherit QM_USER — explicitly on AuditReviewer. Admin/QMManager inherit it transitively.
export const PERMISSION_HIERARCHY = Object.freeze({
  [UserType.Admin]: [
    UserType.QMPilot,
    UserType.ServiceOps,
    UserType.QMManager,
    UserType.ReportViewer,
    UserType.CohereEngOnCall,
    UserType.CoherePm,
    UserType.AuditReviewer,
  ],
  [UserType.QMPilot]: [Permissions.QM_PILOT],
  [UserType.ServiceOps]: [UserType.ServiceOpsGeneral],
  [UserType.ServiceOpsGeneral]: [Permissions.QM_USER, Permissions.FIND_A_CASE],
  [UserType.QMManager]: [UserType.Assigner, Permissions.QM_MANAGER],
  [UserType.Assigner]: [Permissions.QM_USER, Permissions.SUPERVISOR, Permissions.FIND_A_CASE],
  [UserType.ReportViewer]: [Permissions.PRODUCTIVITY_REPORTS],
  [UserType.CohereEngOnCall]: [
    Permissions.QM_MANAGER,
    Permissions.QM_PILOT,
    Permissions.QM_USER,
    Permissions.SUPERVISOR,
    Permissions.FIND_A_CASE,
  ],
  [UserType.CoherePm]: [
    Permissions.QM_MANAGER,
    Permissions.QM_PILOT,
    Permissions.QM_USER,
    Permissions.SUPERVISOR,
    Permissions.FIND_A_CASE,
  ],
  [UserType.NurseReviewer]: [Permissions.QM_USER, Permissions.FIND_A_CASE],
  [UserType.AdminReviewer]: [Permissions.QM_USER, Permissions.FIND_A_CASE],
  [UserType.MDReviewer]: [Permissions.QM_USER, Permissions.FIND_A_CASE],
  [UserType.PharmDReviewer]: [Permissions.QM_USER, Permissions.FIND_A_CASE],
  [UserType.AuditReviewer]: [Permissions.AUDIT_REVIEW, Permissions.FIND_A_CASE],
});

const isAuthorized = generateAuthorizer<Feature>(PERMISSION_HIERARCHY);
